import { fitTitle, fitDescription } from "@/lib/seo-text";
import { createFileRoute, Link } from "@tanstack/react-router";

import { CtaBand, FaqList, PageHero } from "@/components/page-parts";
import michaelOcean from "@/assets/michael-klein-ocean-v4.jpg";
import { FIRM, PEOPLE, PENINSULA_ADDRESS, faqSchema, MICHAEL_PHONE, MICHAEL_PHONE_HREF } from "@/lib/firm";

const MICHAEL = PEOPLE.find((p) => p.slug === "michael-klein")!;

const TITLE = "Michael Klein | Lawyer, Redcliffe & Caloundra | Coastside Law";
const DESCRIPTION =
  "Michael Klein, Legal Practice Director at Coastside Law. Admitted 2003, former managing partner of GKS Law. Property, wills and estates, commercial and litigation.";

const FAQS = [
  {
    q: "Where is Michael Klein practising now?",
    a: `Michael is Legal Practice Director of Coastside Law. The firm has an office at ${FIRM.street}, ${FIRM.suburb}, on the Sunshine Coast, and a Peninsula office at ${PENINSULA_ADDRESS} in Moreton Bay. He works across both and can be reached directly on ${MICHAEL_PHONE}.`,
  },
  {
    q: "Was Michael Klein at GKS Law?",
    a: "Yes. Michael was managing partner of GKS Law for many years before establishing Coastside Law. Clients and referrers who worked with him there are welcome to contact him directly — he continues to act for many of them.",
  },
  {
    q: "When was Michael Klein admitted as a solicitor?",
    a: "Michael was admitted in 2003 and is admitted to both the High Court of Australia and the Supreme Court of Queensland. He has practised for more than two decades.",
  },
  {
    q: "What areas of law does Michael practise in?",
    a: "Property and conveyancing, wills and estates including estate disputes, commercial and business matters, and litigation. His earlier career included extensive insurance and personal injury litigation, acting for both insurers and injured people.",
  },
  {
    q: "How do I get in touch with Michael Klein?",
    a: `Call ${MICHAEL_PHONE} or send an enquiry through this site and it will come straight to him. He is happy to have an initial conversation about a matter before you engage the firm.`,
  },
];

export const Route = createFileRoute("/michael-klein")({
  head: () => ({
    meta: [
      { name: "twitter:card", content: "summary" },
      { title: fitTitle(TITLE) },
      { name: "description", content: fitDescription(DESCRIPTION) },
      { property: "og:title", content: fitTitle(TITLE) },
      { property: "og:description", content: fitDescription(DESCRIPTION) },
      { property: "og:type", content: "profile" },
      { property: "og:url", content: "https://coastsidelaw.com.au/michael-klein" },
    ],
    links: [{ rel: "canonical", href: "https://coastsidelaw.com.au/michael-klein" }],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Person",
          name: "Michael Klein",
          givenName: "Michael",
          familyName: "Klein",
          jobTitle: MICHAEL.role,
          image: MICHAEL.photo.startsWith("http") ? MICHAEL.photo : `https://coastsidelaw.com.au${MICHAEL.photo}`,
          description: DESCRIPTION,
          telephone: MICHAEL_PHONE,
          knowsAbout: [...MICHAEL.areas],
          sameAs: [MICHAEL.linkedin],
          alumniOf: { "@type": "Organization", name: "GKS Law" },
          hasOccupation: {
            "@type": "Occupation",
            name: "Solicitor",
            occupationLocation: { "@type": "State", name: "Queensland" },
          },
          worksFor: {
            "@type": "LegalService",
            name: FIRM.name,
            telephone: MICHAEL_PHONE,
            address: {
              "@type": "PostalAddress",
              streetAddress: FIRM.street,
              addressLocality: FIRM.suburb,
              addressRegion: FIRM.state,
              postalCode: FIRM.postcode,
              addressCountry: FIRM.country,
            },
          },
          workLocation: [
            { "@type": "Place", name: `${FIRM.suburb}, Queensland` },
            { "@type": "Place", name: "Redcliffe, Queensland" },
          ],
        }),
      },
      { type: "application/ld+json", children: JSON.stringify(faqSchema(FAQS)) },
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          itemListElement: [
            { "@type": "ListItem", position: 1, name: "Home", item: "https://coastsidelaw.com.au/" },
            { "@type": "ListItem", position: 2, name: "Our people", item: "https://coastsidelaw.com.au/our-people" },
            { "@type": "ListItem", position: 3, name: "Michael Klein", item: "https://coastsidelaw.com.au/michael-klein" },
          ],
        }),
      },
    ],
  }),
  component: MichaelKleinPage,
});

function MichaelKleinPage() {
  return (
    <>
      <PageHero
        eyebrow="Legal Practice Director"
        title="Michael Klein"
        lead="Solicitor since 2003. Property, wills and estates, commercial and litigation — practising across the Sunshine Coast and the Redcliffe Peninsula."
      />

      <figure className="mx-auto max-w-6xl px-5 pt-12">
        <img
          src={michaelOcean}
          alt="Michael Klein, Legal Practice Director at Coastside Law, by the water on the Queensland coast"
          width={928}
          height={1152}
          className="mx-auto aspect-[928/1152] w-full max-w-md rounded-md object-cover shadow-xl"
        />
      </figure>

      <section className="mx-auto max-w-3xl px-5 py-12">
        <div className="flex flex-col gap-7 sm:flex-row sm:items-start">
          <div>
            <p className="text-sm font-semibold text-accent-foreground">
              {MICHAEL.role} · Admitted {MICHAEL.admitted}
            </p>
            <ul className="mt-4 space-y-1.5 text-sm text-muted-foreground">
              {MICHAEL.courts.map((c) => (
                <li key={c}>Admitted — {c}</li>
              ))}
              <li>{FIRM.membership}</li>
              <li>Based in {MICHAEL.based}</li>
              <li>
                <a
                  href={MICHAEL.linkedin}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="font-semibold text-accent-foreground underline underline-offset-4"
                >
                  Michael on LinkedIn
                </a>
              </li>
            </ul>
            <a
              href={MICHAEL_PHONE_HREF}
              className="mt-5 inline-flex items-center justify-center rounded-sm bg-accent px-5 py-2.5 text-sm font-semibold text-accent-foreground transition-opacity hover:opacity-90"
            >
              Call {MICHAEL_PHONE}
            </a>
          </div>
        </div>

        <div className="mt-12 space-y-5 text-base leading-relaxed text-muted-foreground">
          <h2 className="font-serif text-3xl text-foreground">Background</h2>
          <p>{MICHAEL.bio}</p>
          <p>
            Michael was managing partner of GKS Law for many years before establishing Coastside Law
            with Erin Wilkinson. Clients, real estate agents and referrers who worked with him there
            regularly continue to send him work, and he is happy to hear from anyone who is trying to
            track him down.
          </p>
        </div>

        <div className="mt-12">
          <h2 className="font-serif text-3xl text-foreground">What Michael does</h2>
          <div className="mt-6 grid gap-4 sm:grid-cols-2">
            {[
              {
                to: "/conveyancing",
                title: "Property & conveyancing",
                body: "Residential and commercial sales and purchases, off-the-plan, subdivisions, caveats and title issues.",
              },
              {
                to: "/wills-and-estates",
                title: "Wills & estates",
                body: "Wills, testamentary trusts, probate and letters of administration, and executor advice.",
              },
              {
                to: "/estate-disputes",
                title: "Estate disputes",
                body: "Family provision claims, will validity and undue influence — informed by two decades of litigation.",
              },
              {
                to: "/business-sales",
                title: "Business & commercial",
                body: "Business sales and purchases, leases, management rights and commercial agreements.",
              },
            ].map((item) => (
              <Link
                key={item.to}
                to={item.to}
                className="rounded-sm border border-border bg-card p-5 card-elevate"
              >
                <p className="font-serif text-xl text-foreground">{item.title}</p>
                <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{item.body}</p>
              </Link>
            ))}
          </div>
        </div>

        <div className="mt-12 rounded-sm border-l-2 border-accent bg-secondary/50 p-7">
          <h2 className="font-serif text-2xl text-foreground">Where to find him</h2>
          <p className="mt-3 text-sm leading-relaxed text-muted-foreground">
            Sunshine Coast office: {FIRM.street}, {FIRM.suburb} {FIRM.state} {FIRM.postcode}.
            <br />
            Scarborough office: {PENINSULA_ADDRESS} —{" "}
            <Link to="/scarborough" className="font-semibold text-primary underline">
              see the Scarborough page
            </Link>
            .
            <br />
            Direct line: {MICHAEL_PHONE}.
          </p>
          <Link
            to="/our-people"
            className="mt-5 inline-block text-sm font-semibold text-accent-foreground underline underline-offset-4"
          >
            Meet the rest of the team
          </Link>
        </div>

        <div className="mt-14">
          <h2 className="font-serif text-3xl text-foreground">Questions people ask</h2>
          <div className="mt-6">
            <FaqList items={FAQS} />
          </div>
        </div>
      </section>

      <CtaBand title="Speak with Michael directly" />
      <div className="h-20" />
    </>
  );
}
